import tenantConstants from '@constants';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { WALLET_TYPES } from './wallet';
import { enrichQuotaRowWithProductVisuals } from '../tenant/common/utils/quotaCreditsProductVisualsFallback';

// A quota user pays for every action with one unit of the matching product instead of credits.

// Everything else (services, Golden Pin, Lead Marketplace) is grouped as "Others".
const QUOTA_CORE_PRODUCT_SLUGS = ['basic-listing', 'signature-listing', 'hot-listing', 'refresh'];

export const QUOTA_OTHERS_KEY = 'others';

export const QUOTA_OWNER_ROLE = 'owner';

// The dashboard's "All users" option.
const ALL_USERS_ID = '-1';

export const QUOTA_CART_SOURCE = 'profolio';

const DAY_IN_MS = 86400000;

export const QUOTA_SEARCH_DEBOUNCE_MS = 500;

// IDs come back as numbers or strings depending on the endpoint.
export const isSameId = (a, b) => a != null && b != null && String(a) === String(b);

// A real user picked in a user selector, not "All users".
export const isSpecificUserId = (id) => id != null && id !== '' && String(id) !== ALL_USERS_ID;

// Products the listing actions data does not describe yet.
const EXTRA_PRODUCT_META = {
  'golden-pin': { icon: 'MdLocationOn', iconColor: tenantTheme['warning-color'] },
  'lead-marketplace': { icon: 'FiUsers', iconColor: tenantTheme['primary-color'] },
};

// The backend calls this wallet "credit" (`wallet_type`); without wallet_type, any non-currency user counts.
export const isQuotaUser = (user) =>
  !!tenantConstants.QUOTA_ENABLED &&
  !!user &&
  (user.wallet_type ? user.wallet_type === WALLET_TYPES.CREDIT : user.isCurrencyUser === false);

// Neither the owner nor an admin: only spends units assigned to them.
export const isAgencyAgent = (user) =>
  !!user?.agency && !user?.is_agency_admin && user?.user_role_within_agency !== QUOTA_OWNER_ROLE;

// Quota users get the same listing products as credits users; only how they pay differs.
export const hasFullProductsAccess = (user) => !!user?.isCurrencyUser || isQuotaUser(user);

export const canBuyQuota = (user) => isQuotaUser(user) && !isAgencyAgent(user);

export const canManageAgencyQuota = (user) => isQuotaUser(user) && !!user?.agency && !isAgencyAgent(user);

const getQuotaProductMeta = (slug) => {
  const visuals = enrichQuotaRowWithProductVisuals(slug, EXTRA_PRODUCT_META[slug]);
  const iconColor = visuals.iconColor || tenantTheme['primary-color'];
  return { icon: visuals.icon || 'GiTwoCoins', iconColor, iconProps: { ...visuals.iconProps, color: iconColor } };
};

const QUOTA_PRODUCT_DESCRIPTIONS = {
  'basic-listing': 'Post new listings',
  'signature-listing': 'Most views',
  'hot-listing': 'More visibility',
  refresh: 'Boost to top',
  'photography-service': 'A professional photo shoot',
  'videography-service': 'A professional property video',
  'drone-footage-service': 'Aerial footage of the property',
  'golden-pin': 'Stand out on the map',
  'lead-marketplace': 'One lead from the marketplace',
};

export const getQuotaProductDescription = (slug) => QUOTA_PRODUCT_DESCRIPTIONS[slug] || '';

export const getQuotaProductName = (product) => tenantUtils.getLocalisedString(product, 'name') || product?.slug;

const toCount = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const sortByCoreOrder = (a, b) => QUOTA_CORE_PRODUCT_SLUGS.indexOf(a?.slug) - QUOTA_CORE_PRODUCT_SLUGS.indexOf(b?.slug);


const sumBy = (list, key) => (list || []).reduce((acc, item) => acc + toCount(item?.[key]), 0);

const mapQuotaProduct = (item) => ({
  ...item,
  id: item?.product_id,
  name: getQuotaProductName(item),
  total: toCount(item?.total),
  available: toCount(item?.available),
  used: toCount(item?.used),
  notAssigned: item?.not_assigned != null ? toCount(item.not_assigned) : null,
  withAgents: item?.with_agents != null ? toCount(item.with_agents) : null,
  boughtSeparately: item?.bought_separately != null ? toCount(item.bought_separately) : null,
  isCore: QUOTA_CORE_PRODUCT_SLUGS.includes(item?.slug),
  ...getQuotaProductMeta(item?.slug),
});

// Pool and with-agents numbers are only sent to owners and admins; null otherwise.
const sumOptional = (list, key) => (list.some((e) => e[key] != null) ? sumBy(list, key) : null);

// credits/summary for a quota user: credits_summary.bayut = { package, product_wise }.
export const quotaSummaryMapper = (response) => {
  const body = response?.credits_summary?.bayut || {};
  const products = (body.product_wise || []).map(mapQuotaProduct);
  const others = products.filter((e) => !e.isCore);

  return {
    package: body.package ? { ...body.package, name: tenantUtils.getLocalisedString(body.package, 'name') } : null,
    products,
    core: products.filter((e) => e.isCore).sort(sortByCoreOrder),
    others,
    othersTotals: {
      available: sumBy(others, 'available'),
      used: sumBy(others, 'used'),
      notAssigned: sumOptional(others, 'notAssigned'),
      withAgents: sumOptional(others, 'withAgents'),
    },
    totals: {
      total: sumBy(products, 'total'),
      available: sumBy(products, 'available'),
      used: sumBy(products, 'used'),
      notAssigned: sumOptional(products, 'notAssigned'),
      withAgents: sumOptional(products, 'withAgents'),
    },
  };
};

// get_staff sends each product's total as `assigned`.
export const quotaAgentsMapper = (response) => ({
  agents: (response?.users || []).map((agent) => ({
    ...agent,
    name: tenantUtils.getLocalisedString(agent, 'name'),
    isOwner: !!agent?.is_owner,
    assigned: toCount(agent?.assigned),
    available: toCount(agent?.available),
    used: toCount(agent?.used),
    products: (agent?.products || []).map((row) => mapQuotaProduct({ ...row, total: row?.assigned })),
  })),
  pagination: response?.pagination,
  filterCounts: response?.products_aggs || {},
});

// `price` is VAT included; the `credit` product belongs to credits users.
export const quotaPricesMapper = (response) =>
  (response?.products || [])
    .map((item) => ({ ...mapQuotaProduct({ ...item, product_id: item?.id }), price: toCount(item?.price) }))
    .filter((item) => item.price > 0 && item.slug !== 'credit');

// applicable_products: a per-product user's balances are in `credits.product_wise`, not on each product.
export const withQuotaUnits = (products, credits) => {
  const rows = credits?.product_wise || [];
  if (!rows.length) return products;
  return products.map((product) => {
    const row = rows.find((e) => isSameId(e?.product_id, product?.product_id));
    return row ? { ...product, available_units: row.available } : product;
  });
};

// Agency perks quota users don't see.
const QUOTA_HIDDEN_PACKAGE_BENEFITS = [
  'users',
  'credit_allocation_to_staff',
  'dedicated_account_manager',
  'featured_tag_for_agency',
  'listing_collection_by_bayut',
];
const isShownToQuota = (key) => !QUOTA_HIDDEN_PACKAGE_BENEFITS.includes(key);

export const hideQuotaPackageBenefits = (data) => {
  if (!data?.packageData) return data;
  const withoutHidden = (pkg) =>
    pkg?.additional_info?.options
      ? {
          ...pkg,
          additional_info: {
            ...pkg.additional_info,
            options: Object.fromEntries(
              Object.entries(pkg.additional_info.options).filter(([key]) => isShownToQuota(key)),
            ),
          },
        }
      : pkg;
  return {
    ...data,
    benefits: (data.benefits || []).filter((benefit) => isShownToQuota(benefit?.key)),
    packageData: Object.fromEntries(
      Object.entries(data.packageData).map(([duration, packages]) => [duration, (packages || []).map(withoutHidden)]),
    ),
  };
};

// A package's countable items (listings, users); yes/no perks stay on the package card.
export const getPackageIncludes = (packageData) =>
  Object.entries(packageData?.additional_info?.options || {})
    .map(([key, option]) => ({ key, ...option }))
    .filter((option) => /^\d+$/.test(String(option?.value)))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

// Add-ons wait behind "View All"; with no listing products, the first four are shown instead.
const TOP_UP_INITIAL_COUNT = 4;
export const splitTopUpProducts = (products = []) => {
  const core = products.filter((e) => e?.isCore);
  const rest = products.filter((e) => !e?.isCore);
  return core.length
    ? { shown: core, more: rest }
    : { shown: rest.slice(0, TOP_UP_INITIAL_COUNT), more: rest.slice(TOP_UP_INITIAL_COUNT) };
};

const topUpQuantity = (quantities, product) => Number(quantities?.[product?.id]) || 0;
export const getTopUpTotal = (products = [], quantities = {}) =>
  products.reduce((acc, e) => acc + toCount(e?.price) * topUpQuantity(quantities, e), 0);
export const getTopUpCartLines = (products = [], quantities = {}) =>
  products
    .filter((e) => topUpQuantity(quantities, e) > 0)
    .map((e) => ({
      item_id: e?.id,
      item_type: 'Product',
      source: QUOTA_CART_SOURCE,
      quantity: topUpQuantity(quantities, e),
    }));

// Same shape as the credits consumption summary.
export const quotaUsageBreakdownMapper = (summary) => {
  if (!summary) return null;
  const byProducts = (summary?.products || []).map((item) => ({
    title: item?.name,
    slug: item?.slug,
    icon: item?.icon,
    iconColor: item?.iconColor,
    used_credits: item?.used,
  }));
  const total = sumBy(byProducts, 'used_credits');
  return {
    details: [
      { label: 'Available', value: summary?.totals?.available },
      { label: 'quota_used', value: summary?.totals?.used },
      { label: 'Total', value: summary?.totals?.total },
    ],
    by_products: byProducts,
    chartData: {
      datasets: [
        {
          data: byProducts.map((item) => item?.used_credits),
          backgroundColor: byProducts.map((item) => item?.iconColor || 'rgba(0, 0, 0, 0.2)'),
        },
      ],
      total,
      labels: byProducts.map((item) => item?.title),
    },
  };
};

// Only owners and admins are sent the agency pool.
export const isQuotaAgencyView = (data) => data?.totals?.notAssigned != null;

export const getQuotaSegmentColors = (color) => ({
  main: color,
  soft: `color-mix(in srgb, ${color} 40%, ${tenantTheme.whiteColor})`,
  used: tenantTheme['border-color-normal'],
});

export const getHeldQuotaProducts = (products) =>
  (products || []).filter((product) => product?.total || product?.available || product?.used);

// Package dates are KSA calendar days.
const toPackageDay = (value, time) => new Date(`${String(value).slice(0, 10)}T${time}`);

// Rounded, since a daylight-saving change makes a day 23 or 25 hours.
export const getPackageDaysLeft = (endDate, now = new Date()) => {
  if (!endDate) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((toPackageDay(endDate, '00:00:00') - today) / DAY_IN_MS));
};

export const getPackageTimeline = (pkg, now = new Date()) => {
  if (!pkg?.end_date) return {};
  const end = toPackageDay(pkg.end_date, '23:59:59');
  const start = pkg?.start_date ? toPackageDay(pkg.start_date, '00:00:00') : null;
  const elapsed = start && end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : null;
  return { daysLeft: getPackageDaysLeft(pkg.end_date, now), elapsed };
};

// duration_in_months comes back as a number or a string.
const PACKAGE_DURATION_LABELS = { 12: 'Yearly', 6: 'Half Yearly' };
export const getPackageDurationLabel = (pkg) => PACKAGE_DURATION_LABELS[Number(pkg?.duration_in_months)] || null;
export const isYearlyPackage = (pkg) => Number(pkg?.duration_in_months) === 12;

export const getQuotaTileClickProps = (onClick, label) => ({
  className: 'is-click',
  role: 'button',
  tabIndex: 0,
  'aria-label': label,
  onClick,
  onKeyDown: (e) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault?.();
      onClick?.();
    }
  },
});

const QUOTA_DRAWER_WIDTH = 720;
export const getQuotaSideDrawerProps = (locale) => ({
  width: Math.min(QUOTA_DRAWER_WIDTH, window?.innerWidth || QUOTA_DRAWER_WIDTH),
  placement: locale === 'ar' ? 'left' : 'right',
  rootClassName: 'rounded-side-drawer',
});

export const BOTTOM_SHEET_COLLAPSED_HEIGHT = '72vh';
export const BOTTOM_SHEET_DRAG_THRESHOLD = 40;
export const BOTTOM_SHEET_TAP_DISTANCE = 6;

// Keys get_staff takes in f[products].
export const QUOTA_STAFF_FILTERS = ['all', 'available', 'exhausted', 'not_assigned'];
export const QUOTA_STAFF_FILTER_LABELS = {
  all: 'All',
  available: 'Has products',
  exhausted: 'All used',
  not_assigned: 'Nothing assigned',
};
// DataTable does not translate column titles.
export const getQuotaStaffTableColumns = (t) => [
  { title: t('Staff Details'), dataIndex: 'user_details', key: 'user_details', component: 'StaffDetails' },
  { title: t('Assigned'), dataIndex: 'quota_assigned', key: 'quota_assigned', component: 'Number' },
  { title: t('Available'), dataIndex: 'quota_available', key: 'quota_available', component: 'Number' },
  { title: t('quota_used'), dataIndex: 'quota_used', key: 'quota_used', component: 'Number' },
  { title: t('quota_products_column'), dataIndex: 'quota_products', key: 'quota_products', component: 'Products' },
  { title: t('Actions'), dataIndex: 'rowActions', key: 'rowActions', component: 'UserRowActions' },
];

export const sortOwnerFirst = (list, isOwner = (e) => !!e?.isOwner) =>
  [...(list || [])].sort((a, b) => Number(isOwner(b)) - Number(isOwner(a)));

export const getQuotaPool = (product) => product?.notAssigned ?? product?.available ?? 0;
export const getAgentProductUnits = (agent, productId) =>
  (agent?.products || []).find((e) => isSameId(e?.id, productId))?.available || 0;

export const clampAssignDelta = (value, held, pool) =>
  Math.min(Math.max(Math.round(Number(value) || 0), -(held || 0)), pool || 0);

// credits/transfer takes one direction per call.
export const getQuotaTransferBatches = ({ changes = [], deltas = {}, agentId, ownerId }) => {
  const deltaOf = (product) => deltas?.[product?.id] || 0;
  return {
    takeBack: changes
      .filter((product) => deltaOf(product) < 0)
      .map((product) => ({
        from_user_id: agentId,
        to_user_id: ownerId,
        product_id: product?.id,
        quantity: -deltaOf(product),
      })),
    assign: changes
      .filter((product) => deltaOf(product) > 0)
      .map((product) => ({
        from_user_id: ownerId,
        to_user_id: agentId,
        product_id: product?.id,
        quantity: deltaOf(product),
      })),
  };
};

export const getQuotaModalOkText = (action, selectedPaymentOption) => {
  if (action?.isQuota) return selectedPaymentOption === 'currency' ? 'Continue' : 'Submit';
  return action?.isSufficient ? 'Submit' : 'Continue';
};

// On a listing that is not live yet only the tier can be paid by card.
export const hasQuotaUnits = (item) => Number(item?.availableUnits) > 0;
export const isQuotaTierSelectable = (product, canPayByCard) => hasQuotaUnits(product) || !!canPayByCard;
export const isQuotaServiceSelectable = (service, { isPosted, activeProduct, canPayByCard } = {}) => {
  if (hasQuotaUnits(service)) return !!isPosted || !activeProduct || hasQuotaUnits(activeProduct);
  return !!canPayByCard && !!isPosted;
};
export const getQuotaServiceHint = (service, options = {}) => {
  if (isQuotaServiceSelectable(service, options)) return null;
  return !hasQuotaUnits(service) && !options?.canPayByCard
    ? 'Ask your agency admin for more'
    : 'Add after your listing is live';
};
export const pickQuotaInitialTier = (tiers, { isPosted, canPayByCard } = {}) => {
  if (isPosted) return null;
  const list = tiers || [];
  return (
    list.find((e) => e?.isRecommended && hasQuotaUnits(e)) ||
    list.find((e) => hasQuotaUnits(e)) ||
    (canPayByCard ? list.find((e) => e?.slug === 'signature-listing') : null) ||
    null
  );
};
export const splitQuotaSelection = (items) => {
  const unitItems = (items || []).filter(hasQuotaUnits);
  const cardItems = (items || []).filter((e) => !hasQuotaUnits(e));
  return {
    unitItems,
    cardItems,
    cardTotal: cardItems.reduce((acc, e) => acc + (Number(e?.unitPrice) || 0), 0),
  };
};

export const getQuotaInfoSections = ({ isAgent, canManage } = {}) => [
  {
    id: 1,
    backgroundColor: '#FCF7F1',
    icon: 'CreditAvailableIcon',
    heading: 'Using Products',
    text: 'Basic Listing puts one listing live. Hot Listing and Signature Listing give a listing more visibility for 30 days. Refresh moves a listing back to the top. Add-ons such as Photography, Videography and Drone Footage work the same way: one product for one job.',
    withUsageLink: true,
  },
  {
    id: 2,
    backgroundColor: '#EBF5FF',
    icon: 'CreditUsedIcon',
    heading: 'Run out?',
    text: isAgent
      ? 'Ask your agency admin to assign you more products.'
      : 'Buy more any time from Packages & Products. Pay at checkout in SAR, VAT included.',
  },
  ...(canManage
    ? [
        {
          id: 3,
          backgroundColor: tenantTheme['primary-light-4'],
          icon: 'CreditAvailableIcon',
          heading: 'Your team',
          text: 'Assign products to your agents and take back what they have not used. Products you assign are no longer in your agency pool until you take them back.',
        },
      ]
    : []),
  {
    id: 4,
    backgroundColor: '#ECF7F0',
    icon: 'CreditTimeIcon',
    heading: 'Rejected listings and expiry',
    text: 'If a listing is rejected, the products it used come back to whoever used them. Products expire with the package they came from.',
  },
];
